import { CheckIcon } from "@radix-ui/react-icons";
import {
  ApiError,
  PublicUnsubscribeView,
  PublicUnsubscribesService,
} from "client/openapi";
import { Button, ButtonColor } from "components/Button";
import { ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const STILL_GET =
  "You'll still get messages about your meetings, payments, and forms.";

function Card({ children }: { children: ReactNode }) {
  return (
    // The app pads every page for its navbar; this page has none.
    <div className="-mt-[85px] min-h-screen bg-gray-50 px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-[560px] rounded-2xl bg-white p-6 shadow-sm sm:p-8">
        <div className="py-8 text-center">{children}</div>
      </div>
    </div>
  );
}

/**
 * The page a promotional email's unsubscribe link opens. No login: one
 * click unsubscribes the address from that organization's promotional
 * emails, and a link undoes it.
 */
export default function UnsubscribePage() {
  const { token } = useParams();
  const [view, setView] = useState<PublicUnsubscribeView>();
  const [failed, setFailed] = useState<"invalid" | "load">();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!token) return;
    PublicUnsubscribesService.loadUnsubscribe({ requestBody: { token } })
      .then(setView)
      .catch((e) =>
        setFailed(
          e instanceof ApiError && e.status === 404 ? "invalid" : "load"
        )
      );
  }, [token]);

  function change(unsubscribed: boolean) {
    if (!token) return;
    setBusy(true);
    setError(undefined);
    PublicUnsubscribesService.updateUnsubscribe({
      requestBody: { token, unsubscribed },
    })
      .then(setView)
      .catch(() =>
        setError("That didn't go through. Check your connection and try again.")
      )
      .finally(() => setBusy(false));
  }

  if (failed === "invalid") {
    return (
      <Card>
        <h1 className="text-2xl font-semibold text-gray-900">
          This unsubscribe link isn&apos;t valid.
        </h1>
        <p className="mt-3 text-base text-gray-600">
          Use the link at the bottom of the email you got.
        </p>
      </Card>
    );
  }
  if (failed === "load") {
    return (
      <Card>
        <h1 className="text-2xl font-semibold text-gray-900">
          This page could not be opened.
        </h1>
        <p className="mt-3 text-base text-gray-600">
          Check your connection and reload the page.
        </p>
      </Card>
    );
  }
  if (!view) {
    return (
      <Card>
        <div className="animate-pulse space-y-4">
          <div className="mx-auto h-7 w-2/3 rounded bg-gray-200" />
          <div className="mx-auto h-4 w-1/2 rounded bg-gray-100" />
          <div className="mx-auto h-11 w-40 rounded bg-gray-100" />
        </div>
      </Card>
    );
  }
  if (view.unsubscribed) {
    return (
      <Card>
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white">
          <CheckIcon width={28} height={28} />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">
          You&apos;re unsubscribed.
        </h1>
        <p className="mt-3 text-base text-gray-600">
          {view.email} won&apos;t get promotional emails from {view.org_name}{" "}
          anymore. {STILL_GET}
        </p>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <button
          type="button"
          className="mt-6 text-sm text-gray-500 underline"
          disabled={busy}
          onClick={() => change(false)}
        >
          Unsubscribed by mistake? Subscribe again.
        </button>
      </Card>
    );
  }
  return (
    <Card>
      <h1 className="text-2xl font-semibold text-gray-900">
        Unsubscribe from {view.org_name}&apos;s promotional emails?
      </h1>
      <p className="mt-3 text-base text-gray-600">
        {view.email} will stop getting newsletters, offers, and other
        promotional emails from {view.org_name}. {STILL_GET}
      </p>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-6 flex justify-center">
        <Button
          type="button"
          color={ButtonColor.GREEN}
          disabled={busy}
          onClick={() => change(true)}
        >
          Unsubscribe
        </Button>
      </div>
    </Card>
  );
}
